<template>
  <div class="info-panel">
    <div class="info-panel-header">
      <div class="info-panel-title">{{ $t('campaigns.linkedin.linkedin_job_advertisement') }}</div>
      <IconHelpHexagonFilled class="info-help-icon" @click="openHelpModal = true" />

      <HelpModal v-model="openHelpModal" context="linkedin,basic" />
    </div>
    <div class="info-panel-description">
      {{ $t('campaigns.linkedin.linkedin_description') }}
    </div>
  </div>

  <LinkedInCompanyPage
    :provider-id="props.providerId"
    :open-card="openCard"
    :completedCards="completedCards"
    :object="props.object"
    @completed="
      (currentCard, nextCard) => {
        openCard = nextCard;
      }
    "
    @toggle="handleFormCardToggle"
    @update:object="(obj) => (tempObj = obj)" />

  <LinkedInJobDetails
    :provider-id="props.providerId"
    :open-card="openCard"
    :completedCards="completedCards"
    :object="props.object"
    @completed="
      (currentCard, nextCard) => {
        openCard = nextCard;
      }
    "
    @toggle="handleFormCardToggle"
    :fieldGroups="fieldGroups"
    @update:object="(obj) => (tempObj = obj)" />

  <LinkedInLocation
    :provider-id="props.providerId"
    :open-card="openCard"
    :completedCards="completedCards"
    :object="props.object"
    @completed="
      (currentCard, nextCard) => {
        openCard = nextCard;
      }
    "
    @toggle="handleFormCardToggle"
    :fieldGroups="fieldGroups"
    @update:object="(obj) => (tempObj = obj)" />

  <LinkedInTC
    :provider-id="props.providerId"
    :open-card="openCard"
    :completedCards="completedCards"
    @completed="
      (currentCard, nextCard) => {
        openCard = nextCard;
      }
    "
    @toggle="handleFormCardToggle" />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { IconHelpHexagonFilled } from '@tabler/icons-vue';
import HelpModal from '@/components/orders/HelpModal.vue';
import type { FieldGroup } from '@/types/types';
import LinkedInCompanyPage from '@/components/form/LinkedInCompanyPage.vue';
import LinkedInJobDetails from '@/components/form/LinkedInJobDetails.vue';
import LinkedInLocation from '@/components/form/LinkedInLocation.vue';
import LinkedInTC from '@/components/form/LinkedInTC.vue';
import translator from '@/locale/translator';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';

export interface LinkedInForm {
  company_urn: string | null;
  company_name: string | null;
  workplace_type: string;
  experience_level: string;
  employment_status: string;
  location_id: string | null;
  location_name: string | null;
}

const props = defineProps<{
  providerId: string | null | undefined;
  fieldGroups: FieldGroup[];
  object: any;
}>();

const emit = defineEmits<{
  (e: 'update:object', obj: any): void;
}>();

const openHelpModal = ref(false);
const openCard = ref<string | null>(null);
const providerStore = useProviderStore();
const { completedDecks } = storeToRefs(providerStore);
const tempObj = ref({ ...props.object });

const completedCards = computed(() => {
  if (!props.providerId) return new Set<string>();
  return completedDecks.value[props.providerId] || new Set<string>();
});

onMounted(() => {
  openCard.value = props.object?.groups[0].name ?? 'LinkedIn Company Page';
});

const handleFormCardToggle = (title: string) => {
  // If the clicked card is already open, close it
  if (openCard.value === title) {
    openCard.value = null;
  } else {
    // Otherwise, open the clicked card (and close any other open card)
    openCard.value = title;
  }
};

watch(
  () => props.object,
  (newVal) => {
    // If the campaign is already confirmed, mark all cards as completed
    if (newVal?.confirmed || newVal?.values_valid) {
      newVal.groups.forEach((group: any) => {
        providerStore.addCompletedCard(group.name, props.providerId ?? '');
      });
    }

    // T&C consent isn't part of values_valid (the backend has no such field) - only
    // treat it as already given when the campaign was actually confirmed before.
    if (newVal?.confirmed) {
      providerStore.addCompletedCard(translator.get('campaigns.linkedin.terms_and_conditions'), props.providerId ?? '');
    }
  },
  { immediate: true }
);

// watch if all the completed cards are filled, then update the object so it can be confirmed without refresh
watch(
  completedCards,
  (newVal) => {
    if (props.fieldGroups.length > 0 && newVal.size === props.fieldGroups.length + 1) {
      emit('update:object', tempObj.value);
    }
  },
  { deep: true }
);
</script>
<style scoped lang="scss">
.info-panel {
  background-color: #f7fcfc;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  align-self: flex-start;

  .info-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    .info-help-icon {
      color: var(--tblr-primary);
      cursor: pointer;
    }

    .info-panel-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      color: #101828;
    }
  }

  .info-panel-description {
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    color: #313234;
  }
}
</style>
